/**
 * Word-level difference between what a student said and the corrected form, so
 * the UI can highlight only the words that changed ("my sister [work] in a
 * hospital" vs "my sister [works] in a hospital") instead of the whole sentence.
 */

export interface CorrectionSegment {
  text: string;
  /** True for the words that differ between the two sentences. */
  changed: boolean;
}

export interface CorrectionDiff {
  /** The student's sentence, with her mistakes marked. */
  wrong: CorrectionSegment[];
  /** The corrected sentence, with the fixes marked. */
  right: CorrectionSegment[];
}

interface Token {
  text: string;
  key: string;
  isWord: boolean;
}

/** Splits a sentence into words and the whitespace between them (both kept). */
function tokenize(sentence: string): Token[] {
  const tokens: Token[] = [];
  const re = /(\s+)|([^\s]+)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(sentence)) !== null) {
    if (match[1] !== undefined) {
      tokens.push({ text: match[1], key: '', isWord: false });
    } else {
      const text = match[2]!;
      tokens.push({ text, key: normalizeWord(text), isWord: true });
    }
  }
  return tokens;
}

/** Comparison key: case- and punctuation-insensitive so "Monday." matches "monday". */
function normalizeWord(word: string): string {
  return word
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/^[^\p{L}\p{N}']+|[^\p{L}\p{N}']+$/gu, '');
}

/** Longest common subsequence over word keys; returns the matched index pairs. */
function matchWords(a: Token[], b: Token[]): Array<[number, number]> {
  const wordsA = a.map((t, i) => ({ i, key: t.key })).filter((t) => a[t.i]!.isWord && t.key);
  const wordsB = b.map((t, i) => ({ i, key: t.key })).filter((t) => b[t.i]!.isWord && t.key);
  const n = wordsA.length;
  const m = wordsB.length;
  const table: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      table[i]![j] =
        wordsA[i]!.key === wordsB[j]!.key
          ? table[i + 1]![j + 1]! + 1
          : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
    }
  }
  const pairs: Array<[number, number]> = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (wordsA[i]!.key === wordsB[j]!.key) {
      pairs.push([wordsA[i]!.i, wordsB[j]!.i]);
      i += 1;
      j += 1;
    } else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
      i += 1;
    } else {
      j += 1;
    }
  }
  return pairs;
}

function toSegments(tokens: Token[], matched: Set<number>): CorrectionSegment[] {
  const segments: CorrectionSegment[] = [];
  const push = (text: string, changed: boolean) => {
    const last = segments[segments.length - 1];
    if (last && last.changed === changed) {
      last.text += text;
    } else {
      segments.push({ text, changed });
    }
  };
  tokens.forEach((token, index) => {
    if (token.isWord) {
      // Pure punctuation ("...", "?") has no key and is never a mistake by itself.
      push(token.text, token.key !== '' && !matched.has(index));
      return;
    }
    // Whitespace joins a mark only when it sits between two changed words.
    const previousChanged = segments[segments.length - 1]?.changed ?? false;
    push(token.text, previousChanged && nextWordChanged(tokens, index, matched) === true);
  });
  return segments;
}

function nextWordChanged(tokens: Token[], from: number, matched: Set<number>): boolean | null {
  for (let i = from + 1; i < tokens.length; i += 1) {
    const token = tokens[i]!;
    if (token.isWord) return token.key !== '' && !matched.has(i);
  }
  return null;
}

/**
 * Marks the words that differ between the student's sentence and its correction.
 * When the two sentences share no word at all, everything is marked (the whole
 * phrase was rewritten).
 */
export function diffCorrection(wrong: string, right: string): CorrectionDiff {
  const a = tokenize(wrong);
  const b = tokenize(right);
  const pairs = matchWords(a, b);
  const matchedA = new Set(pairs.map(([i]) => i));
  const matchedB = new Set(pairs.map(([, j]) => j));
  return {
    wrong: toSegments(a, matchedA),
    right: toSegments(b, matchedB),
  };
}
